import { createBrowserRouter, Navigate, type RouteObject, RouterProvider } from "react-router";
import type { ReactNode } from "react";
import { ErrorPage } from "@/views/pages/error/ErrorPage";
import { LoginPage } from "@/views/pages/login/LoginPage";
import { AdminLayout } from "@/views/layouts/AppLayout";
import { systemManagentRoutes } from "./modules/systemManagent";
import { manageCenterRoutes } from "./modules/manageCenter";
import { userOperationRoutes } from "./modules/userOperation";
import { supplierManagementRoutes } from "./modules/supplierManagement";
import { productManagementRoutes } from "./modules/productManagement";
import { activityProductDetailHistoryRoutes, productActivityRoutes } from "./modules/productActivity";
import { tradeManageRoutes } from "./modules/tradeManage";
import { intelligentAssistantRoutes } from "./modules/intelligentAssistant";
import { lianYunUserRoutes } from "./modules/lianYunUser";
import { MyTaskPage } from "@/views/pages/myTask/MyTaskPage";
import { MyTaskNewsListPage } from "@/views/pages/myTask/MyTaskNewsListPage";
import { logisticsRoutes } from "./modules/logistics";
import { expenseManagementRoutes } from "./modules/expenseManagement";
import { indexResearchRoutes } from "./modules/indexResearch";
import { purchasingManageRoutes } from "./modules/purchasingManage";
import { dashboardRoutes } from "./modules/dashboard";
import { useUserStore } from "@/stores/react/authStore";
import { buildMenuRoutes, getFirstVisibleMenuPath } from "./menuRoutes";

export interface AdminRouteHandle {
    title?: string;
    permissions?: string[];
    isKeepAlive?: boolean;
    /** 隐藏详情、表单等页面进入后，需要保持高亮的可见菜单完整路径。 */
    activeMenu?: string;
}

export type AdminRouteObject = RouteObject & {
    handle?: AdminRouteHandle;
    children?: AdminRouteObject[];
};

/**
 * 还原旧 Vue 根路由守卫：登录用户访问 `/` 时进入第一个有权限的可见菜单页面。
 * 目标从同一份菜单树计算，确保默认入口与头部、侧栏的权限和隐藏规则一致。
 */
export function DefaultRouteRedirect() {
    const permissions = useUserStore(state => state.userInfo.permissions || []);
    const targetPath = getFirstVisibleMenuPath(buildMenuRoutes(staticRoutes, permissions)) || "/403";

    return <Navigate replace to={targetPath} />;
}

/** 模板结构路由入口，后续按一级业务模块拆分至 views/router/modules。 */
export const staticRoutes: AdminRouteObject[] = [
    { path: "/", element: <DefaultRouteRedirect />, handle: { title: "首页" } },
    {
        path: "/myTask",
        element: <AdminLayout />,
        handle: { title: "待办工作", permissions: ["ROLE_OUTSTANDING_R"] },
        children: [
            {
                // 沿用 Vue 根路由 redirect，进入待办工作时落到历史 index 地址。
                index: true,
                element: <Navigate replace to="index" />,
                handle: { title: "待办工作", permissions: ["ROLE_OUTSTANDING_R"] }
            },
            // 保留 Vue /myTask/index 历史入口。
            {
                path: "index",
                element: <MyTaskPage />,
                handle: { title: "待办工作", permissions: ["ROLE_OUTSTANDING_R"] }
            },
            {
                path: "newsList",
                element: <MyTaskNewsListPage />,
                handle: { title: "动态列表", permissions: ["ROLE_OUTSTANDING_R"] }
            }
        ]
    },
    { path: "/login", element: <LoginPage />, handle: { title: "登录" } },
    dashboardRoutes,
    systemManagentRoutes,
    manageCenterRoutes,
    userOperationRoutes,
    supplierManagementRoutes,
    productManagementRoutes,
    productActivityRoutes,
    activityProductDetailHistoryRoutes,
    tradeManageRoutes,
    logisticsRoutes,
    expenseManagementRoutes,
    purchasingManageRoutes,
    intelligentAssistantRoutes,
    lianYunUserRoutes,
    indexResearchRoutes,
    { path: "/403", element: <ErrorPage status={403} />, handle: { title: "403页面" } },
    { path: "/404", element: <ErrorPage status={404} />, handle: { title: "404页面" } },
    { path: "/500", element: <ErrorPage status={500} />, handle: { title: "500页面" } },
    { path: "*", element: <Navigate replace to="/404" /> }
];

const hasRoutePermission = (route: AdminRouteObject, permissions: string[]) => {
    const required = route.handle?.permissions || [];
    return required.length === 0 || required.some((permission: string) => permissions.includes(permission));
};

/** 递归过滤动态菜单，沿用 Vue 版本任一权限命中规则。 */
export const filterDynamicRoutes = (routes: AdminRouteObject[], permissions: string[]): AdminRouteObject[] =>
    routes.reduce<AdminRouteObject[]>((result, route) => {
        const children = route.children ? filterDynamicRoutes(route.children, permissions) : [];
        if ((route.children && children.length > 0) || (!route.children && hasRoutePermission(route, permissions))) {
            result.push(route.children ? { ...route, children } : route);
        }
        return result;
    }, []);

export interface RouteAccessGuardProps {
    permissions?: string[];
    children: ReactNode;
}

/** React 路由守卫：沿用 Vue 的登录拦截和任一权限命中规则。 */
export function RouteAccessGuard({ permissions = [], children }: RouteAccessGuardProps) {
    const token = useUserStore(state => state.token);
    const userPermissions = useUserStore(state => state.userInfo.permissions || []);

    if (!token) {
        return <Navigate replace to="/login" />;
    }
    if (permissions.length > 0 && !permissions.some(permission => userPermissions.includes(permission))) {
        return <Navigate replace to="/403" />;
    }
    return <>{children}</>;
}

/** 登录与错误页沿用 Vue 白名单，未登录用户也必须能够访问这些公开入口。 */
const publicRoutePaths = new Set(["/login", "/403", "/404", "/500", "*"]);

/** 在真正创建 data router 前为页面元素注入权限守卫，避免只过滤菜单而放开直达 URL。 */
export const withRouteAccessGuards = (routes: AdminRouteObject[]): AdminRouteObject[] =>
    routes.map(route => {
        const element =
            route.element && !publicRoutePaths.has(route.path || "") ? (
                <RouteAccessGuard permissions={route.handle?.permissions}>{route.element}</RouteAccessGuard>
            ) : (
                route.element
            );
        return route.children
            ? { ...route, element, children: withRouteAccessGuards(route.children) }
            : { ...route, element };
    });

/** 创建浏览器路由实例，供应用入口和测试注入动态路由。 */
export const createAdminRouter = (dynamicRoutes: AdminRouteObject[] = []) =>
    createBrowserRouter(
        withRouteAccessGuards([...staticRoutes.slice(0, -1), ...dynamicRoutes, staticRoutes[staticRoutes.length - 1]!]),
        {
            basename: import.meta.env.VITE_PUBLIC_PATH || "/"
        }
    );

export function AdminRouterProvider({ dynamicRoutes = [] }: { dynamicRoutes?: AdminRouteObject[] }): ReactNode {
    return <RouterProvider router={createAdminRouter(dynamicRoutes)} />;
}
